<nz-drawer
  [nzVisible]="isOpen"
  [nzWidth]="isMaximized ? '100vw' : '50vw'"
  [nzTitle]="drawerTitle"
  (nzOnClose)="close()"
>
  <ng-template #drawerTitle>
    <div class="flex items-center justify-between w-full">
      <span>AI Agent Trace</span>
      <div class="flex items-center gap-2">
        <button
          nz-button
          nzType="default"
          nzSize="small"
          [nzLoading]="isExporting"
          (click)="generateDebugDocumentation()"
          class="flex items-center gap-1.5"
        >
          <i class="fa-solid fa-download"></i>
          <span>Generate Debug Documentation</span>
        </button>

        <button
          nz-button
          nzType="text"
          nzSize="small"
          (click)="setExpandAll(true)"
          class="flex items-center text-secondary-text hover:text-primary-text transition-colors"
          nz-tooltip
          nzTooltipTitle="Expand All"
        >
          <i class="fa-regular fa-square-plus text-base"></i>
        </button>

        <button
          nz-button
          nzType="text"
          nzSize="small"
          (click)="setExpandAll(false)"
          class="flex items-center text-secondary-text hover:text-primary-text transition-colors"
          nz-tooltip
          nzTooltipTitle="Collapse All"
        >
          <i class="fa-regular fa-square-minus text-base"></i>
        </button>

        <button
          nz-button
          nzType="text"
          nzSize="small"
          (click)="toggleMaximize()"
          class="flex items-center text-secondary-text hover:text-primary-text transition-colors"
          nz-tooltip
          [nzTooltipTitle]="isMaximized ? 'Exit Fullscreen' : 'Fullscreen'"
        >
          <i
            class="fa-solid"
            [ngClass]="isMaximized ? 'fa-compress' : 'fa-expand'"
          ></i>
        </button>
      </div>
    </div>
  </ng-template>
  <ng-container *nzDrawerContent>
    <div *ngIf="isLoading" class="p-8 flex justify-center items-center">
      <nz-spin nzSimple></nz-spin>
    </div>

    <div
      *ngIf="errorMessage"
      class="p-4 text-danger bg-danger/10 border border-danger/20 rounded mb-4"
    >
      {{ errorMessage }}
    </div>

    <div
      *ngIf="!isLoading && trace.length === 0 && !errorMessage"
      class="text-secondary-text p-4"
    >
      No debug trace recorded for this message.
    </div>

    <div *ngIf="!isLoading && trace.length > 0" class="p-4">
      <!-- User Input Message -->
      <div *ngIf="userMessageInput" class="mb-6 flex justify-end">
        <div
          class="bg-alt-background border border-primary-border text-primary-text p-3 rounded-2xl rounded-tr-sm max-w-[85%] shadow-sm text-sm whitespace-pre-wrap word-break-break-word"
        >
          {{ userMessageInput }}
        </div>
      </div>

      <!-- Trace Summary -->
      <div
        *ngIf="traceSummary"
        class="mb-6 p-4 bg-component-inner border border-primary-border rounded-lg shadow-sm"
      >
        <h4
          class="text-sm font-semibold text-primary-text mb-2 flex items-center gap-2 m-0"
        >
          <i class="fa-solid fa-chart-simple text-primary"></i> Statistics
        </h4>
        <div class="text-sm text-secondary-text leading-relaxed">
          <span class="font-bold text-primary-text">{{
            traceSummary.totalSteps
          }}</span>
          steps, executed in
          <span class="font-bold text-primary-text"
            >{{ traceSummary.totalDurationMs | number }}ms</span
          >. The top 2 longest steps (<span
            class="font-medium text-primary-text bg-primary/10 px-1 rounded"
            >{{ traceSummary.top2StepsNames }}</span
          >) took
          <span class="font-bold text-primary-text"
            >{{ traceSummary.top2DurationMs | number }}ms</span
          >
          (<span class="font-bold text-primary-text"
            >{{ traceSummary.top2Percentage }}%</span
          >
          of total duration).
        </div>
      </div>

      <nz-timeline>
        <nz-timeline-item *ngFor="let node of trace; let i = index">
          <div class="mb-6">
            <div
              class="flex items-center gap-3 mb-2 cursor-pointer select-none"
              (click)="node.isExpanded = !node.isExpanded"
            >
              <i
                class="fa-solid fa-chevron-right transition-transform text-secondary-text text-xs"
                [class.rotate-90]="node.isExpanded"
              ></i>
              <span
                *ngIf="!node.tasks || node.tasks.length === 0"
                class="text-sm font-bold text-primary-text px-2 py-1 bg-alt-background border border-secondary-border rounded shadow-sm"
              >
                {{ node.nodeName }}
              </span>
              <span
                *ngIf="node.tasks && node.tasks.length > 0"
                class="text-sm font-bold text-primary-text flex items-center"
              >
                <span
                  class="bg-primary/10 text-primary px-2 py-0.5 rounded shadow-sm mr-2 font-mono text-xs"
                  >{{ i + 1 }}</span
                >
                <ng-container
                  *ngIf="node.source === 'input'; else taskNamesTemplate"
                >
                  input
                </ng-container>
                <ng-template #taskNamesTemplate>
                  {{ getTaskNames(node) }}
                </ng-template>
              </span>
              <span class="text-xs text-secondary-text ml-auto">
                {{ node.createdAt | date: 'mediumTime' }}
              </span>
              <div
                *ngIf="node.durationMs! > 0"
                class="flex items-center gap-1.5 ml-2"
              >
                <span
                  class="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full"
                >
                  {{ node.durationMs }}ms
                </span>
                <span
                  *ngIf="node.durationPercentage"
                  class="text-[10px] text-secondary-text bg-alt-background border border-secondary-border px-1.5 py-0.5 rounded-full"
                >
                  {{ node.durationPercentage }}%
                </span>
                <span
                  *ngIf="node.isMaxDuration"
                  class="text-[10px] bg-danger text-white px-1.5 py-0.5 rounded-full font-bold"
                >
                  MAX
                </span>
              </div>
            </div>

            <!-- Node Body -->
            <div *ngIf="node.isExpanded" class="pl-6">
              <!-- Tasks as Cards -->
              <div
                *ngIf="node.tasks && node.tasks.length > 0"
                class="flex flex-col gap-3 mb-4"
              >
                <div
                  *ngFor="let task of node.tasks"
                  class="bg-component-inner border border-primary-border rounded-xl shadow-sm p-3"
                >
                  <div
                    class="flex items-center gap-2 mb-2 cursor-pointer select-none"
                    (click)="task.isExpanded = !task.isExpanded"
                  >
                    <i
                      class="fa-solid fa-chevron-right transition-transform text-secondary-text text-xs"
                      [class.rotate-90]="task.isExpanded"
                    ></i>
                    <span
                      class="font-bold text-primary-text text-sm bg-primary/10 text-primary px-2 py-0.5 rounded shadow-sm"
                      >{{ task.name }}</span
                    >
                  </div>

                  <!-- Task Body -->
                  <div *ngIf="task.isExpanded" class="pl-5 pt-1">
                    <!-- Task Result -->
                    <div *ngIf="task.result !== undefined" class="mt-2">
                      <div
                        class="text-xs font-semibold text-secondary-text mb-1 uppercase"
                      >
                        Result
                      </div>
                      <div
                        class="bg-background rounded p-2 text-xs overflow-auto max-h-[200px] border border-primary-border custom-scrollbar"
                      >
                        <rapider-json-viewer
                          [data]="task.result"
                        ></rapider-json-viewer>
                      </div>
                    </div>

                    <!-- Task Interrupts -->
                    <div
                      *ngIf="task.interrupts && task.interrupts.length > 0"
                      class="mt-2"
                    >
                      <div
                        class="text-xs font-semibold text-warning mb-1 uppercase"
                      >
                        Interrupts
                      </div>
                      <div
                        class="bg-warning/10 text-warning rounded p-2 text-xs overflow-auto max-h-[200px] border border-warning/30 custom-scrollbar"
                      >
                        <rapider-json-viewer
                          [data]="task.interrupts"
                        ></rapider-json-viewer>
                      </div>
                    </div>

                    <!-- Task Error -->
                    <div *ngIf="task.error" class="mt-2">
                      <div
                        class="text-xs font-semibold text-danger mb-1 uppercase"
                      >
                        Error
                      </div>
                      <div
                        class="bg-danger/10 text-danger rounded p-2 text-xs overflow-auto max-h-[200px] border border-danger/30 custom-scrollbar"
                      >
                        <rapider-json-viewer
                          [data]="task.error"
                        ></rapider-json-viewer>
                      </div>
                    </div>

                    <!-- Task AI Log Section -->
                    <div *ngIf="task.aiLogId" class="mt-3">
                      <button
                        *ngIf="!task.aiLogData"
                        nz-button
                        nzType="dashed"
                        [nzLoading]="task.isAiLogLoading"
                        (click)="loadTaskAiLog(task); $event.stopPropagation()"
                      >
                        <i class="fa-solid fa-file-code mr-2"></i> AI Log Detail
                      </button>

                      <nz-collapse
                        *ngIf="task.aiLogData"
                        [nzBordered]="false"
                        class="mt-3 bg-transparent p-0"
                      >
                        <nz-collapse-panel
                          [nzHeader]="aiLogHeader"
                          [(nzActive)]="task.aiLogData.isExpanded"
                          class="bg-background border border-primary-border rounded-xl shadow-sm mb-0"
                        >
                          <ng-template #aiLogHeader>
                            <div class="flex flex-col w-full pr-4">
                              <div class="flex items-center gap-2">
                                <span
                                  class="font-bold text-sm text-primary-text"
                                  >AI Log Detail</span
                                >
                                <span
                                  class="text-xs text-secondary-text ml-auto whitespace-nowrap"
                                >
                                  {{
                                    task.aiLogData.createdDate | date: 'medium'
                                  }}
                                </span>
                              </div>
                              <div class="mt-2 flex items-center">
                                <span
                                  class="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded shadow-sm font-mono border border-primary/20 flex-shrink-0"
                                >
                                  <i class="fa-solid fa-microchip mr-1"></i>
                                  {{
                                    task.aiLogData.providerName | lowercase
                                  }}.{{
                                    task.aiLogData.providerVersion | lowercase
                                  }}
                                </span>
                              </div>
                            </div>
                          </ng-template>

                          <div class="pt-2">
                            <!-- FunctionName and Tokens Sentence -->
                            <div class="flex flex-col gap-2 mb-4 px-4">
                              <div class="flex items-center gap-2 flex-wrap">
                                <span
                                  *ngIf="task.aiLogData.functionName"
                                  class="text-xs bg-secondary-background text-primary-text px-2 py-0.5 rounded shadow-sm border border-secondary-border cursor-help"
                                  nz-tooltip
                                  [nzTooltipTitle]="task.aiLogData.id"
                                >
                                  <i class="fa-solid fa-code mr-1"></i>
                                  {{ task.aiLogData.functionName }}
                                </span>
                              </div>

                              <div class="text-xs text-secondary-text m-0">
                                <i class="fa-solid fa-coins mr-1"></i>
                                <span class="font-medium text-primary-text">{{
                                  task.aiLogData.inputTokenCount || 0
                                }}</span>
                                input token<ng-container
                                  *ngIf="task.aiLogData.cachedInputTokenCount"
                                  >,
                                  <span class="font-medium text-primary-text">{{
                                    task.aiLogData.cachedInputTokenCount
                                  }}</span>
                                  cache</ng-container
                                >,
                                <span class="font-medium text-primary-text">{{
                                  task.aiLogData.outputTokenCount || 0
                                }}</span>
                                output token
                              </div>
                            </div>

                            <!-- System Prompt Section -->
                            <nz-collapse
                              *ngIf="task.aiLogData.systemPromptText"
                              [nzBordered]="false"
                              class="bg-transparent -mx-2 mb-2"
                            >
                              <nz-collapse-panel
                                nzHeader="System Prompt"
                                [nzActive]="true"
                                class="bg-transparent border-0"
                              >
                                <div
                                  class="bg-component-inner border border-primary-border rounded p-4 overflow-auto max-h-[400px] custom-scrollbar text-sm"
                                >
                                  <rapider-markdown-preview
                                    [data]="task.aiLogData.systemPromptText"
                                    [isTransparent]="true"
                                  ></rapider-markdown-preview>
                                </div>
                              </nz-collapse-panel>
                            </nz-collapse>

                            <!-- Input / Output Section -->
                            <nz-collapse
                              [nzBordered]="false"
                              class="bg-transparent -mx-2"
                            >
                              <nz-collapse-panel
                                nzHeader="Input Object"
                                [nzActive]="true"
                                class="bg-transparent border-0 mb-2"
                              >
                                <div
                                  class="bg-component-inner border border-primary-border rounded p-2 overflow-auto max-h-[300px] custom-scrollbar"
                                >
                                  <ng-container
                                    *ngIf="
                                      isObject(task.aiLogData.curl);
                                      else textCurl
                                    "
                                  >
                                    <rapider-json-viewer
                                      [data]="task.aiLogData.curl"
                                    ></rapider-json-viewer>
                                  </ng-container>
                                  <ng-template #textCurl>
                                    <pre
                                      class="text-xs whitespace-pre-wrap font-mono m-0 text-primary-text"
                                      >{{ task.aiLogData.curl }}</pre
                                    >
                                  </ng-template>
                                </div>
                              </nz-collapse-panel>
                              <nz-collapse-panel
                                nzHeader="Output Object"
                                [nzActive]="true"
                                class="bg-transparent border-0"
                              >
                                <div
                                  class="bg-component-inner border border-primary-border rounded p-2 overflow-auto max-h-[300px] custom-scrollbar"
                                >
                                  <ng-container
                                    *ngIf="
                                      isObject(task.aiLogData.response);
                                      else textResponse
                                    "
                                  >
                                    <rapider-json-viewer
                                      [data]="task.aiLogData.response"
                                    ></rapider-json-viewer>
                                  </ng-container>
                                  <ng-template #textResponse>
                                    <pre
                                      class="text-xs whitespace-pre-wrap font-mono m-0 text-primary-text"
                                      >{{ task.aiLogData.response }}</pre
                                    >
                                  </ng-template>
                                </div>
                              </nz-collapse-panel>
                            </nz-collapse>
                          </div>
                        </nz-collapse-panel>
                      </nz-collapse>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Events (Collapsible) -->
              <nz-collapse
                *ngIf="node.events && node.events.length > 0"
                [nzBordered]="false"
                class="bg-component-inner border border-primary-border rounded-xl shadow-sm overflow-hidden mt-4"
              >
                <nz-collapse-panel
                  [nzHeader]="'Events (' + node.events.length + ')'"
                  [(nzActive)]="node.isEventsExpanded"
                >
                  <div
                    class="flex flex-col gap-2 p-2 bg-background rounded border border-primary-border max-h-[400px] overflow-auto custom-scrollbar"
                  >
                    <div
                      *ngFor="let event of node.events"
                      class="p-3 border border-secondary-border bg-component-inner rounded hover:border-primary/50 transition-colors"
                    >
                      <div class="flex items-center gap-2 mb-2 flex-wrap">
                        <span
                          class="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded font-mono shadow-sm border border-primary/20"
                        >
                          #{{ event.sequence }}
                        </span>
                        <span
                          class="text-sm font-bold text-primary-text flex-1"
                        >
                          {{ event.eventType }}
                        </span>
                        <span
                          *ngIf="event.status"
                          class="text-xs px-2 py-0.5 rounded shadow-sm border capitalize"
                          [ngClass]="{
                            'bg-success/10 text-success border-success/20':
                              event.status === 'success',
                            'bg-danger/10 text-danger border-danger/20':
                              event.status === 'error' ||
                              event.status === 'failed',
                            'bg-warning/10 text-warning border-warning/20':
                              event.status === 'pending',
                          }"
                        >
                          {{ event.status }}
                        </span>
                        <span
                          class="text-xs text-secondary-text whitespace-nowrap"
                        >
                          {{ event.createdDate | date: 'mediumTime' }}
                        </span>
                        <span
                          *ngIf="event.durationMs"
                          class="text-xs bg-alt-background text-secondary-text px-2 py-0.5 rounded shadow-sm border border-secondary-border"
                        >
                          <i class="fa-regular fa-clock mr-1"></i
                          >{{ event.durationMs }}ms
                        </span>
                      </div>

                      <div
                        *ngIf="event.title || event.subtitle"
                        class="mb-3 px-1 border-l-2 border-primary/30 pl-2"
                      >
                        <div
                          *ngIf="event.title"
                          class="text-sm text-heading font-medium"
                        >
                          {{ event.title }}
                        </div>
                        <div
                          *ngIf="event.subtitle"
                          class="text-xs text-secondary-text mt-0.5"
                        >
                          {{ event.subtitle }}
                        </div>
                      </div>

                      <div *ngIf="event.data" class="mt-2">
                        <div
                          class="text-[10px] font-semibold text-secondary-text mb-1 uppercase tracking-wider"
                        >
                          Event Data
                        </div>
                        <div
                          class="bg-background border border-primary-border/50 rounded p-2 text-xs overflow-auto max-h-[250px] custom-scrollbar"
                        >
                          <rapider-json-viewer
                            [data]="event.data"
                          ></rapider-json-viewer>
                        </div>
                      </div>
                    </div>
                  </div>
                </nz-collapse-panel>
              </nz-collapse>

              <!-- Iterative State JSON Viewer (Collapsible) -->
              <nz-collapse
                *ngIf="node.stateIterative && node.stateIterative.length > 0"
                [nzBordered]="false"
                class="bg-component-inner border border-primary-border rounded-xl shadow-sm overflow-hidden mt-4"
              >
                <nz-collapse-panel
                  [nzHeader]="'State'"
                  [(nzActive)]="node.isStateExpanded"
                >
                  <div
                    class="max-h-[500px] overflow-auto custom-scrollbar font-mono text-sm p-2 bg-background rounded border border-primary-border"
                  >
                    <div class="text-secondary-text">{{ '{' }}</div>
                    <div class="pl-4 flex flex-col gap-0">
                      <div
                        *ngFor="let change of node.stateIterative"
                        class="flex items-start gap-1"
                      >
                        <!-- Colored Key -->
                        <span
                          class="whitespace-nowrap pt-0.5"
                          [ngClass]="{
                            'text-success': change.type === 'added',
                            'text-[#D4A30A] dark:text-[#EAB308]':
                              change.type === 'updated',
                            'text-danger line-through opacity-70':
                              change.type === 'deleted',
                            'text-primary-text': change.type === 'unchanged',
                          }"
                        >
                          "{{ change.key }}":
                        </span>

                        <!-- Value rendering -->
                        <div
                          class="flex-1 min-w-0"
                          [ngClass]="{
                            'line-through text-danger opacity-70':
                              change.type === 'deleted',
                          }"
                        >
                          <rapider-json-viewer
                            [data]="change.value"
                          ></rapider-json-viewer>
                        </div>
                      </div>
                    </div>
                    <div class="text-secondary-text">{{ '}' }}</div>
                  </div>
                </nz-collapse-panel>
              </nz-collapse>
            </div>
          </div>
        </nz-timeline-item>
      </nz-timeline>
    </div>
  </ng-container>
</nz-drawer>

<!-- Debug Documentation Modal -->
<nz-modal
  [(nzVisible)]="isDebugModalVisible"
  nzTitle="Debug Documentation"
  (nzOnCancel)="closeDebugModal()"
  [nzWidth]="'80vw'"
  [nzFooter]="debugModalFooter"
>
  <ng-container *nzModalContent>
    <div
      class="bg-component-inner border border-primary-border rounded p-4 overflow-auto max-h-[70vh] custom-scrollbar text-sm"
    >
      <rapider-markdown-preview
        *ngIf="debugDocumentationContent"
        [data]="debugDocumentationContent"
        [isTransparent]="true"
      ></rapider-markdown-preview>
      <div *ngIf="isLoadingDebugDoc" class="flex justify-center p-8">
        <nz-spin nzSimple></nz-spin>
      </div>
    </div>
  </ng-container>
  <ng-template #debugModalFooter>
    <div class="flex items-center justify-end gap-2">
      <button nz-button nzType="default" (click)="copyDebugDoc()">
        <i class="fa-regular fa-copy mr-1"></i> Copy
      </button>
      <button nz-button nzType="primary" (click)="downloadDebugDoc()">
        <i class="fa-solid fa-download mr-1"></i> Download
      </button>
    </div>
  </ng-template>
</nz-modal>
